import { styled, css } from 'styled-components';
import {
  Accordion,
  Button,
  ButtonWithStates,
  Checkbox,
  crTheme,
  Input,
  Lookup,
  Select,
  Text,
} from '@comicrelief/component-library';

const ButtonWrapper = styled.div`
  margin-top: 2.5rem;
  display: flex;
  button {
    width: 100%;
    background: ${crTheme.color('red')};
  }
`;

const Heading = styled(Text)<MarginProps>`
  margin: ${({ margin }) => {
    switch (margin) {
      case 'l':
        return '2rem 0 2rem 0';
      case 'm':
        return '1rem 0 1rem 0';
      case 's':
        return '0.5rem 0 0.5rem 0';
      default:
        return '0';
    }
  }};
`;

const HorizontalLine = styled.hr<MarginProps>`
  color: ${crTheme.color('grey_medium')};
  border-style: solid;
  border-width: 1px 0 0;
  margin: ${({ $margin }) => {
    switch ($margin) {
      case 'l':
        return '2rem 0 2rem 0';
      case 'm':
        return '1.5rem 0 1.5rem 0';
      case 's':
        return '1rem 0 1rem 0';
      default:
        return '0';
    }
  }};
`;

interface MarginProps {
  $margin?: 's' | 'm' | 'l';
}

const OutlineWrapper = styled.div`
  border: 1px solid ${crTheme.color('grey')};
  background-color: ${crTheme.color('white')};
  border-radius: 1rem;
  padding: 25px 30px;
`;

const PostcodeSearchWrapper = styled.div`
  margin-top: 1rem;
  gap: 1rem;
  > div > div > label > div > input {
    // overrides the max-width of the input from the CL
    max-width: 100%;
  }
      > div > div > label > span {
    // overrides the max-width of the input from the CL
    max-width: 100%;
  }
  > div {
    @media ${crTheme.allBreakpoints('M')} {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    button {
      border: none;
      height: 48px;
      background-color: ${crTheme.color('blue_dark')};
      color: ${crTheme.color('white')};
      &:hover, &:active, &:focus {
        background: ${crTheme.color('blue')};
        color: ${crTheme.color('white')};
      }
      margin: 0.5rem 0 0;
      @media ${crTheme.allBreakpoints('M')} {
        align-self: flex-start;
        margin: 1.7rem 0 0 0.5rem;
        width: 30%;
      }
    }
  }
`;

// Handy snippet to reuse in a few contexts:
const ReusableInputStyles = css`
  input, select {
    background-color: ${crTheme.color('white')};
    @media ${crTheme.allBreakpoints('M')} {
     background-color: ${crTheme.color('grey_light')};
    }
  }

   // Error icon import fix
  input + div[class*="ErrorIconWrapper"] {
    background: url("/assets/images/error-alert-icon-red.svg") center center / contain no-repeat;
    --iconSize: 19px;
    width: var(--iconSize);
    height: var(--iconSize);
    z-index: 3;
  }
`;

const ReusableStylesFocusVisibleOnWrapper = css`
  /* Add focus styles to the wrapper when the hidden checkbox is focused */
  cursor: pointer;
  &:focus-within {
    outline: 2px solid ${crTheme.color('blue_donate')};
    outline-offset: 2px;
  }
`;

const ReusableStylesHideInputElement = css`
  /* button or checkbox is hidden but still present for being able to use tab to navigate */
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const StyledCheckbox = styled(Checkbox)`
  // Tick icon import fix
  &:checked + span {
    background: url("/assets/images/tick-white.svg") center center / contain no-repeat ${crTheme.color('red')} !important;
  }

  + span + span {
    color: ${crTheme.color('black')};
  }
`;

const StyledFieldset = styled.fieldset`
  border: none;
  padding: 0;
  margin: 0;
`;

const StyledButtonWithStates = styled(ButtonWithStates)`
  && {
    margin: 0;
  }
`;

const StyledLookup = styled(Lookup)`
  ${ReusableInputStyles}

  // This is the animation that shows in the Search button when search is running:
  > button > div > span {
    display: flex;
  }

  // This is the container for the input field for the postcode lookup and its label:
  > div {
    width: 100%;
  }

  // This is the container for the results of the postcode lookup. This allows it to sit properly in the DOM as it better suits this design/layout:
  > div > div {
    position: relative;
    margin-top: 1rem;
    border: 1px solid ${crTheme.color('grey')};
    border-radius: 0.5rem 0 0 0.5rem;

    ul {
      li {
        padding: 0.5rem 1.5rem;
      }
    }
  }
`;

const StyledSelect = styled(Select)`
  ${ReusableInputStyles}
  select {
    max-width: 100%;
    // Icon import fix - conditionally apply background based on hideArrow prop
    background: ${({ hideArrow }) => (hideArrow
    ? 'rgb(244, 243, 245)'
    : 'url("/assets/images/drop-down.svg") calc(100% - 1.5rem) 14px / 20px 1.5rem no-repeat rgb(244, 243, 245)')
};
  }
`;

const SubmitButton = styled(Button)`
  margin-top: 30px;
  @media ${crTheme.allBreakpoints('L')} {
    margin: 30px 0 0 !important;
  }
`;

// Those below this line sit outside of the alphabetical order of the components above, owing to their dependencies on other components in this file.

const FullWidthStyledInput = styled(Input)`
  ${ReusableInputStyles}
  @media ${crTheme.allBreakpoints('M')} {
    input {
      max-width: none;
    }
  }

  &.darkLabel {
    span {
      color: ${crTheme.color('black')};
    }
  }
`;

interface AmountInputProps {
  isPayin?: boolean;
  hasError?: boolean;
}

const AmountInput = styled(FullWidthStyledInput)<AmountInputProps>`

  // Hide the standard error message for this context, since we use
  // the 'structured' error message below for the formatting options
  span[data-test="error-message"] {
    display: none;
  }

  // Label
  > span {
    ${({ isPayin }) => (Boolean(isPayin) && css`
      display: none;
    `)}

    margin: 20px 0 10px;

    @media ${crTheme.allBreakpoints('M')} {
      margin-top: 22px;
    }
  }

  input {
    // Removes increase/decrease arrows
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &[type='number'] {
      -moz-appearance: textfield;
    }
  }
`;

const StyledAccordion = styled(Accordion)`
  justify-content: center;

  && {
    box-shadow: none;
    background-color: transparent;

    > div {
      transition: none;
      padding: 0;
    }

    button {
      background-color: transparent;
      font-size: 0.75rem;
      padding: 0.5rem;
      font-weight: 700;
      justify-content: center;
      gap: 0.25rem;

      &:focus {
        box-shadow:inset 0px 0px 0px 2px ${crTheme.color('blue')};
      }

      > div {
        animation: none;

        svg {
          margin-top: 1px;
          width: 12px;
          height: auto;
        }
      }

      &[aria-expanded="true"] {
        + div {
          // Prevents link underlines being cut off
          padding-bottom: 3px;
        }
      }
    }
  }
`;

// Styles used across 'ShippingDetails', 'PayinBillingAddress' AND
// 'GocardlessConfirmation', hence the *very* generic naming:
const ReviewDetailsWrapper = styled.div`
  background-color: ${crTheme.color('white')};
  border: 2px solid ${crTheme.color('grey')};
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin: 1rem 0;
  
  @media ${crTheme.allBreakpoints('M')} {
    background-color: ${crTheme.color('grey_light')};
  }
`;

const AddressField = styled.span`
  display: block;
`;

const AddressFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const UseOtherAddressWrapper = styled.div`
  margin-bottom: 2rem;
`;

const EditDetailsButton = styled(StyledButtonWithStates)`
  // Increase specificity to override CL styles:
  && {
    margin-top: 1.5em;
    outline: 2px solid ${crTheme.color('dark_grey')};
  
    @media ${crTheme.allBreakpoints('M')} {
      // Recreate the 'grey_light' button without having
      // to mess with breakpoints and props within the render:
      background-color: ${crTheme.color('grey_light')};
      
      &:hover,
      &.focus {
        background-color: ${crTheme.color('black')};
        outline-color: ${crTheme.color('black')};
        color: ${crTheme.color('white')};
      }
    }
  }
`;

export {
  StyledAccordion,
  AmountInput,
  ButtonWrapper,
  FullWidthStyledInput,
  Heading,
  HorizontalLine,
  OutlineWrapper,
  PostcodeSearchWrapper,
  ReusableStylesFocusVisibleOnWrapper,
  ReusableStylesHideInputElement,
  ReusableInputStyles,
  StyledButtonWithStates,
  StyledCheckbox,
  StyledFieldset,
  StyledLookup,
  StyledSelect,
  SubmitButton,
  ReviewDetailsWrapper,
  AddressField,
  AddressFieldsWrapper,
  UseOtherAddressWrapper,
  EditDetailsButton
};
